import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useTheme } from '../context/ThemeContext'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { ArrowLeft, Trophy, Clock, Target, TrendingUp } from 'lucide-react'

const Container = styled.div`
  padding: 40px;
  background-color: ${props => props.theme.backgroundColor};
  min-height: calc(100vh - 80px);
  width: 100%;
  margin: 0;

  @media (max-width: 768px) {
    padding: 20px;
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 30px;
`

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background-color: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  color: ${props => props.theme.textPrimary};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
`

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin: 0;
  flex: 1;
`

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
`

const SummaryCard = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 16px;
`

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`

const SummaryContent = styled.div`
  flex: 1;
`

const SummaryLabel = styled.div`
  font-size: 13px;
  color: ${props => props.theme.textSecondary};
  margin-bottom: 4px;
`

const SummaryValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
`

const RaceSection = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`

const RaceTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin: 0 0 30px 0;
`

const RaceItem = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`

const RaceLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`

const RaceName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
`

const RaceValue = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
`

const RaceBarContainer = styled.div`
  position: relative;
  width: 100%;
  height: 40px;
  background-color: #e5e7eb;
  border-radius: 20px;
  overflow: hidden;
  display: flex;
`

const RaceBar = styled.div`
  height: 100%;
  width: ${props => props.$width}%;
  background: ${props => props.$isCorrect
    ? 'linear-gradient(90deg, #10b981 0%, #34d399 100%)'
    : 'linear-gradient(90deg, #ef4444 0%, #f87171 100%)'
  };
  transition: width 0.5s ease-out;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  ${props => props.$isCorrect ? 'border-radius: 20px 0 0 20px;' : 'border-radius: 0 20px 20px 0;'}
`

const BarLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: white;
  display: flex;
  align-items: center;
  gap: 6px;
`

const BarBadge = styled.div`
  font-size: 11px;
  font-weight: 700;
  background-color: rgba(255, 255, 255, 0.3);
  padding: 4px 8px;
  border-radius: 12px;
  color: white;
`

const QuestionList = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const QuestionItem = styled.div`
  padding: 20px;
  border-radius: 8px;
  background-color: ${props => props.theme.hoverBackground};
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`

const QuestionNumber = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.theme.textSecondary};
  margin-bottom: 8px;
`

const QuestionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  margin-bottom: 12px;
`

const QuestionDetail = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  font-size: 14px;
`

const DetailItem = styled.div`
  color: ${props => props.theme.textSecondary};

  span {
    font-weight: 600;
    color: ${props => props.theme.textPrimary};
  }
`

export default function HistoryDetailPage() {
  const { theme } = useTheme()
  const navigate = useNavigate()
  const { gameId } = useParams()
  const location = useLocation()
  const [game, setGame] = useState(null)

  useEffect(() => {
    // location.state에서 게임 데이터 가져오기
    if (location.state?.game) {
      setGame(location.state.game)
    }
  }, [location.state])

  if (!game) {
    return (
      <Container theme={theme}>
        <Header>
          <BackButton theme={theme} onClick={() => navigate('/history')}>
            <ArrowLeft size={20} />
            뒤로 가기
          </BackButton>
        </Header>
        <p>게임 데이터를 불러올 수 없습니다.</p>
      </Container>
    )
  }

  // 플레이어별 정답/오답 계산
  const playerStats = {}
  game.results?.forEach((result) => {
    const playerName = result.player_name || '플레이어'
    if (!playerStats[playerName]) {
      playerStats[playerName] = {
        correct: 0,
        incorrect: 0,
        totalScore: 0
      }
    }
    if (result.is_correct) {
      playerStats[playerName].correct += 1
    } else {
      playerStats[playerName].incorrect += 1
    }
    playerStats[playerName].totalScore += result.score_delta || 0
  })

  // 문제별 통계 계산 (question_id 기준으로 그룹화)
  const questionStats = {}
  game.results?.filter(r => r.question_id !== -1).forEach((result) => {
    const qId = result.question_id
    if (!questionStats[qId]) {
      questionStats[qId] = {
        title: result.question_title || result.question,
        correctAnswer: result.correct_answer,
        totalResponses: 0,
        correctCount: 0,
        totalTime: 0,
        answerCounts: {}
      }
    }
    questionStats[qId].totalResponses += 1
    if (result.is_correct) {
      questionStats[qId].correctCount += 1
    }
    const timeSpent = parseFloat(result.time_spent) || 0
    questionStats[qId].totalTime += timeSpent

    // 선지별 응답 집계 (DB 컬럼명: submitted_answer)
    const answer = result.submitted_answer || result.player_answer || '무응답'
    questionStats[qId].answerCounts[answer] = (questionStats[qId].answerCounts[answer] || 0) + 1
  })

  const accuracy = Math.round((game.correctAnswers / game.totalQuestions) * 100)

  return (
    <Container theme={theme}>
      <Header>
        <BackButton theme={theme} onClick={() => navigate('/history')}>
          <ArrowLeft size={20} />
          뒤로 가기
        </BackButton>
        <Title theme={theme}>{game.title}</Title>
      </Header>

      <SummaryCards>
        <SummaryCard theme={theme}>
          <IconWrapper>
            <Trophy size={24} />
          </IconWrapper>
          <SummaryContent>
            <SummaryLabel theme={theme}>총 점수</SummaryLabel>
            <SummaryValue theme={theme}>{game.score}</SummaryValue>
          </SummaryContent>
        </SummaryCard>

        <SummaryCard theme={theme}>
          <IconWrapper>
            <Target size={24} />
          </IconWrapper>
          <SummaryContent>
            <SummaryLabel theme={theme}>정답률</SummaryLabel>
            <SummaryValue theme={theme}>{accuracy}%</SummaryValue>
          </SummaryContent>
        </SummaryCard>

        <SummaryCard theme={theme}>
          <IconWrapper>
            <Clock size={24} />
          </IconWrapper>
          <SummaryContent>
            <SummaryLabel theme={theme}>소요 시간</SummaryLabel>
            <SummaryValue theme={theme}>{game.duration}</SummaryValue>
          </SummaryContent>
        </SummaryCard>

        <SummaryCard theme={theme}>
          <IconWrapper>
            <TrendingUp size={24} />
          </IconWrapper>
          <SummaryContent>
            <SummaryLabel theme={theme}>정답 / 전체</SummaryLabel>
            <SummaryValue theme={theme}>{game.correctAnswers} / {game.totalQuestions}</SummaryValue>
          </SummaryContent>
        </SummaryCard>
      </SummaryCards>

      {/* 레이스 형태 시각화 */}
      <RaceSection theme={theme}>
        <RaceTitle theme={theme}>
          최종 순위
        </RaceTitle>
        {Object.entries(playerStats)
          .sort((a, b) => b[1].totalScore - a[1].totalScore) // 점수 높은 순으로 정렬
          .map(([playerName, stats], index) => {
            const total = stats.correct + stats.incorrect
            const correctPercent = total > 0 ? (stats.correct / total) * 100 : 0
            const incorrectPercent = total > 0 ? (stats.incorrect / total) * 100 : 0

            return (
              <RaceItem key={playerName}>
                <RaceLabel>
                  <RaceName theme={theme}>
                    #{index + 1} {playerName}
                  </RaceName>
                  <RaceValue theme={theme}>
                    {stats.totalScore}점
                  </RaceValue>
                </RaceLabel>
                <RaceBarContainer>
                  <RaceBar $width={correctPercent} $isCorrect={true}>
                    <BarLabel>
                      ✓ {stats.correct}
                    </BarLabel>
                  </RaceBar>
                  <RaceBar $width={incorrectPercent} $isCorrect={false}>
                    <BarLabel>
                      ✗ {stats.incorrect}
                    </BarLabel>
                  </RaceBar>
                </RaceBarContainer>
              </RaceItem>
            )
          })}
        {Object.keys(playerStats).length === 0 && (
          <p style={{ textAlign: 'center', color: theme.textSecondary }}>
            데이터가 없습니다.
          </p>
        )}
      </RaceSection>

      {/* 문제별 상세 정보 */}
      <QuestionList theme={theme}>
        <RaceTitle theme={theme}>문제별 상세 정보</RaceTitle>
        {Object.entries(questionStats)
          .sort((a, b) => Number(a[0]) - Number(b[0]))
          .map(([qId, stats], index) => {
            const correctRate = stats.totalResponses > 0
              ? Math.round((stats.correctCount / stats.totalResponses) * 100)
              : 0
            const wrongRate = 100 - correctRate
            const avgTime = stats.totalResponses > 0
              ? (stats.totalTime / stats.totalResponses).toFixed(1)
              : 0

            return (
              <QuestionItem key={qId} theme={theme}>
                <QuestionNumber theme={theme}>문제 {index + 1}</QuestionNumber>
                <QuestionTitle theme={theme}>{stats.title}</QuestionTitle>
                <QuestionDetail theme={theme}>
                  <DetailItem theme={theme}>
                    정답률/오답률: <span style={{ color: '#10b981' }}>{correctRate}%</span> / <span style={{ color: '#ef4444' }}>{wrongRate}%</span>
                  </DetailItem>
                  <DetailItem theme={theme}>
                    평균 소요시간: <span>{avgTime}초</span>
                  </DetailItem>
                </QuestionDetail>
                <DetailItem theme={theme} style={{ marginTop: '8px' }}>
                  선지별 응답률: {
                    [0, 1, 2, 3].map((choiceIdx) => {
                      const choiceKey = String(choiceIdx)
                      const count = stats.answerCounts[choiceKey] || 0
                      const percent = stats.totalResponses > 0 ? Math.round((count / stats.totalResponses) * 100) : 0
                      const isCorrect = choiceKey === String(stats.correctAnswer)
                      return (
                        <span key={choiceIdx} style={{ marginRight: '12px' }}>
                          <span style={{
                            fontWeight: isCorrect ? 700 : 400,
                            color: isCorrect ? '#10b981' : theme.textPrimary
                          }}>
                            {choiceIdx + 1}번 {percent}%{isCorrect ? ' ✓' : ''}
                          </span>
                        </span>
                      )
                    })
                  }
                </DetailItem>
              </QuestionItem>
            )
          })}
      </QuestionList>
    </Container>
  )
}
